import onetime from "@-/com.npmjs.onetime";
import type { GraphemeComponentPrevalenceLookup } from "@-/dev.improve.haniverse.lib.morpheme.grapheme.decomposition";

import componentPrevalenceData from "#component-prevalence.gen.json" with { type: "json" };

/**
 * Precomputed simplified + traditional component-prevalence closures — the two
 * `{ commonComponentSet, uncommonComponentSet, rareComponentSet }` lookups `getGraphemePrevalence`
 * needs.
 *
 * These are a whole-decomposition-graph BFS result that is fully determined by the static
 * dataset (PRC tier data + Taiwan MOE charts), so they are computed once at static-DB build time
 * into `component-prevalence.gen.json` (see `-build-component-prevalence.ts`) instead of being
 * recomputed on-device on every app startup — which previously also forced loading the entire
 * ~20k-row decomposition map (~1s of native SQLite time) just to feed the traversal. This module
 * is the hand-written, typed wrapper that rehydrates the generated arrays into Sets.
 */
export type ComponentPrevalenceLookups = Readonly<{
  simplified: GraphemeComponentPrevalenceLookup;
  traditional: GraphemeComponentPrevalenceLookup;
}>;

function toLookup(serialized: {
  common: readonly string[];
  rare: readonly string[];
  uncommon: readonly string[];
}): GraphemeComponentPrevalenceLookup {
  return {
    commonComponentSet: new Set(serialized.common),
    rareComponentSet: new Set(serialized.rare),
    uncommonComponentSet: new Set(serialized.uncommon),
  };
}

export const getComponentPrevalenceLookups = onetime((): ComponentPrevalenceLookups => ({
  simplified: toLookup(componentPrevalenceData.simplified),
  traditional: toLookup(componentPrevalenceData.traditional),
}));

/** Per-script {@link getGradedGraphemeClosures} sets. */
export type GradedGraphemeClosures = Readonly<{
  simplified: ReadonlySet<string>;
  traditional: ReadonlySet<string>;
}>;

/**
 * Every graded character — any HSK syllabus (simplified) or TBCL (traditional) — together with
 * every component reachable from one, down to the strokes. A primitive in this set is a building
 * block a graded learner actually meets, so the browsing corpus counts it as common even when it
 * is absent from the PRC table / Taiwan MOE charts.
 */
export const getGradedGraphemeClosures = onetime((): GradedGraphemeClosures => ({
  simplified: new Set(componentPrevalenceData.simplified.graded),
  traditional: new Set(componentPrevalenceData.traditional.graded),
}));
